import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { StatusTier } from "@/core/dtos/status-tier";
import { formatPrice } from "@/lib";
import {
  calculateDynamicRate,
  calculateTotalPayout,
  getStatusBonus,
} from "@/lib/miles-calculations";
import { TransactionTypes } from "@/stores/user-credentials";
import { MilesSlider } from "./MilesSlider";

interface MilesProgramCardProps {
  statusTiers: StatusTier[];
  airline: string;
  logo: string;
  loyaltyStatus: StatusTier | null;
  ratePerMile: number;

  sellPrices: Array<{ minRange: number; maxRange: number; price: number }>;
  buyPrices: Array<{ minRange: number; maxRange: number; price: number }>;

  minimumValueForSell: number;
  maximumValueForSell: number;

  minimumValueForBuy: number;
  maximumValueForBuy: number;

  totalMiles: number;
  rejectedMilesAmount?: number;
  /** Labels the total; an offer we adjusted reads as an updated quote. */
  quoteLabel?: string;
  promoCode?: {
    id: string;
    code: string;
    discount:
      | number
      | {
          type: "percentage" | "fixed";
          value: number;
        };
    name: string;
    enabledFor?: string;
    expirationDate?: Date;
  };
  onStatusChange: (status: StatusTier | null) => void;
  onMilesChange: (miles: number) => void;

  transactionType: TransactionTypes;
}

export const MilesProgramCard = ({
  statusTiers,
  airline,
  logo,
  loyaltyStatus,
  ratePerMile,

  minimumValueForBuy,
  maximumValueForBuy,

  minimumValueForSell,
  maximumValueForSell,

  onMilesChange,
  onStatusChange,
  sellPrices = [],
  buyPrices = [],
  totalMiles,
  rejectedMilesAmount,
  quoteLabel = "Updated Quote",
  promoCode,
  transactionType,
}: MilesProgramCardProps) => {
  const dynamicRate = calculateDynamicRate({
    miles: totalMiles,
    transactionType,
    sellPrices,
    buyPrices,
    fallbackRate: ratePerMile,
  });
  const { baseAmount, statusBonusAmount, promoCodeDiscount, totalPayout } =
    calculateTotalPayout({
      totalMiles,
      dynamicRate,
      loyaltyStatus,
      transactionType,
      promoCode,
    });

  const handleMilesChange = (value: number[]) => {
    const newMiles = value[0];
    onMilesChange?.(newMiles);
  };

  const minMiles =
    transactionType === TransactionTypes.BUY
      ? minimumValueForBuy
      : minimumValueForSell;
  const maxMiles =
    transactionType === TransactionTypes.BUY
      ? maximumValueForBuy
      : maximumValueForSell;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl xl:rounded-3xl w-full mx-auto flex flex-col gap-2 overflow-hidden p-2 px-4 xl:p-6">
      {/* Header */}
      <div className="flex flex-row items-center justify-between md:mb-6 gap-4">
        <div className="flex items-start gap-1 xl:gap-3">
          <img
            className="w-12 h-12 rounded flex items-center justify-center text-white text-xs font-bold"
            src={logo}
            alt="Airline logo"
          />
          <div>
            <h3 className="font-semibold text-gray-900 text-sm xl:text-lg">
              {airline}
            </h3>
            <div className="text-sm text-gray-600">
              {formatPrice({
                price: dynamicRate * 100,
                fractionDigits: 2,
              })}
              /100 Miles
            </div>
            {loyaltyStatus && loyaltyStatus.name !== "No Status" && (
              <p className="text-[9px] xl:text-sm text-accent font-bold xl:font-semibold uppercase bg-light-blue w-fit rounded-full px-2 py-[3px] mt-1">
                {loyaltyStatus.name} member
              </p>
            )}
          </div>
        </div>
        <div className="text-right leading-tight">
          <p className="text-accent text-xl xl:text-2xl font-bold text-right">
            {totalMiles.toLocaleString()}
          </p>
          {rejectedMilesAmount && (
            <p className="line-through text-[14px] xl:text-lg font-medium text-right">
              {rejectedMilesAmount.toLocaleString()} Miles
            </p>
          )}
        </div>
      </div>

      <hr className="border-gray-200 my-2 xl:mt-0 xl:mb-4" />

      <MilesSlider
        transactionType={transactionType}
        totalMiles={totalMiles}
        minMiles={minMiles}
        maxMiles={maxMiles}
        handleMilesChange={handleMilesChange}
      />

      {/* Loyalty Status - Only available for sell transactions */}
      {transactionType === TransactionTypes.SELL && (
        <div className="flex flex-col gap-3 xl:gap-8 w-full">
          <div>
            <Select
              value={loyaltyStatus?.name || undefined}
              onValueChange={(value) => {
                if (value === "noStatus") {
                  onStatusChange(null);
                } else {
                  const selectedStatus = statusTiers.find(
                    (status) => status.name === value,
                  );
                  if (selectedStatus) {
                    onStatusChange(selectedStatus);
                  }
                }
              }}
            >
              <SelectTrigger className="w-full h-12 text-gray-500 rounded-sm">
                <SelectValue placeholder="Select Loyalty Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem key="NoLoyaltyStatusOption" value="noStatus">
                  No loyalty status
                </SelectItem>
                {statusTiers?.length > 0 ? (
                  statusTiers.map((status, index) => (
                    <SelectItem key={index} value={status.name}>
                      {status.name}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-1.5 text-sm text-gray-500 cursor-default">
                    No loyalty status tiers available
                  </div>
                )}
              </SelectContent>
            </Select>
            {loyaltyStatus !== null && (
              <p className="text-xs text-blue-600 mt-2">
                +{getStatusBonus(loyaltyStatus)}% bonus for {loyaltyStatus?.name}{" "}
                Members
              </p>
            )}
          </div>
        </div>
      )}

      {/* Pricing Breakdown */}
      <div className="space-y-4 bg-[#F9F9F9] p-3 px-2 xl:p-6 rounded-sm">
        <div className="flex justify-between items-center mb-2 xl:mb-4">
          <span className="font-medium text-gray-900">Base Amount</span>
          <span className="font-semibold text-gray-900">
            {formatPrice({
              price: baseAmount,
              fractionDigits: 2,
            })}
          </span>
        </div>
        {/* Status Bonus - only show if there's a bonus */}
        {loyaltyStatus !== null && statusBonusAmount > 0 && (
          <div className="flex justify-between items-center">
            <p className="font-medium ">
              Loyalty Bonus{" "}
              <span className="text-blue-600 font-semibold">
                {loyaltyStatus?.name}
              </span>
            </p>
            <span className="font-semibold text-blue-600">
              {formatPrice({ price: statusBonusAmount })}
            </span>
          </div>
        )}
        {promoCodeDiscount !== 0 && (
          <div className="flex justify-between items-center">
            <p className="font-medium ">
              Promo Code{" "}
              <span className="text-blue-600 font-semibold">
                {promoCode?.name}
              </span>
            </p>
            <span className="font-semibold text-blue-600">
              {formatPrice({ price: promoCodeDiscount })}
            </span>
          </div>
        )}
        <div className="flex justify-between items-center border-t text-lg xl:text-xl pt-2 text-accent">
          <span className="font-medium">{quoteLabel}</span>
          <span className="font-semibold">
            {formatPrice({ price: totalPayout })}
          </span>
        </div>
      </div>
    </div>
  );
};
